import axiosClient from "utils/axios";
import { ApiList, ApiResponse } from "types/api.types";
import {
  CreateMaterialInOutPayload,
  MaterialInOut,
  MaterialInOutListParams,
  MaterialInOutsTableResponse,
  ReturnMaterialInOutPayload,
  UpdateMaterialInOutPayload,
} from "features/MaterialInOuts/types/material-in-out.types";

const MATERIAL_IN_OUT_BASE_PATH = "/material-in-outs";

function buildMaterialInOutListQueryParams(
  input: MaterialInOutListParams,
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {};
  const setPositiveInt = (key: string, v: number | undefined) => {
    if (v != null && Number.isFinite(v) && Number(v) > 0) {
      params[key] = Number(v);
    }
  };
  const setNonEmpty = (key: string, v: string | undefined) => {
    if (v != null && String(v).trim() !== "") {
      params[key] = String(v).trim();
    }
  };

  if (input.page != null) params.page = input.page;
  if (input.limit != null) params.limit = input.limit;
  setNonEmpty("search", input.search);
  setNonEmpty("sourceType", input.sourceType);
  setPositiveInt("sourceId", input.sourceId);
  setPositiveInt("userId", input.userId);
  setPositiveInt("materialId", input.materialId);
  if (input.outstandingOnly != null) {
    params.outstandingOnly = input.outstandingOnly;
  }

  return params;
}

export const getMaterialInOuts = async (
  input: MaterialInOutListParams = {},
) => {
  try {
    const response = await axiosClient.get<
      ApiResponse<MaterialInOutsTableResponse>
    >(MATERIAL_IN_OUT_BASE_PATH, {
      params: buildMaterialInOutListQueryParams(input),
    });
    return response.data;
  } catch (err) {
    console.warn("Error fetching Material In/Outs : ", err);
    throw err;
  }
};

export const getMaterialInOut = async (id: number) => {
  try {
    if (!Number.isInteger(id) || id <= 0) {
      throw new Error("Invalid ID");
    }
    const response = await axiosClient.get<ApiResponse<MaterialInOut>>(
      `${MATERIAL_IN_OUT_BASE_PATH}/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching Material In/Out : ", err);
    throw err;
  }
};

export const createMaterialInOut = async (
  payload: CreateMaterialInOutPayload,
) => {
  try {
    const response = await axiosClient.post<ApiResponse<MaterialInOut>>(
      MATERIAL_IN_OUT_BASE_PATH,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error creating Material In/Out : ", err);
    throw err;
  }
};

export const updateMaterialInOut = async (
  id: number,
  payload: UpdateMaterialInOutPayload,
) => {
  try {
    if (!Number.isInteger(id) || id <= 0) {
      throw new Error("Invalid ID");
    }
    const response = await axiosClient.put<ApiResponse<MaterialInOut>>(
      `${MATERIAL_IN_OUT_BASE_PATH}/${id}`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error updating Material In/Out : ", err);
    throw err;
  }
};

export const deleteMaterialInOut = async (id: number) => {
  try {
    const response = await axiosClient.delete<ApiResponse<MaterialInOut>>(
      `${MATERIAL_IN_OUT_BASE_PATH}/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error deleting Material In/Out : ", err);
    throw err;
  }
};

/** POST /material-in-outs/:id/return — marks an open row as returned; `dateIn` defaults to now server-side when omitted. */
export const returnMaterialInOut = async (
  id: number,
  payload?: ReturnMaterialInOutPayload,
) => {
  try {
    const response = await axiosClient.post<ApiResponse<MaterialInOut>>(
      `${MATERIAL_IN_OUT_BASE_PATH}/${id}/return`,
      payload ?? {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error returning Material In/Out : ", err);
    throw err;
  }
};

/** GET /material-in-outs/out — dashboard widget; no params, same row shape as the list. */
export const getMaterialsOut = async () => {
  try {
    const response = await axiosClient.get<
      ApiResponse<ApiList<MaterialInOut[]>>
    >(`${MATERIAL_IN_OUT_BASE_PATH}/out`);
    return response.data;
  } catch (err) {
    console.warn("Error fetching materials out : ", err);
    throw err;
  }
};
